/**
 * Task 3.7 — per-member time row.
 *
 * Layout: link-styled Start picker → arrow icon → link-styled End picker
 * → duration label + clock icon, with a hover-revealed Delete IconButton
 * (CSS-only hover per CORRECTIONS rule — NOT JS state). Adding a further
 * schedule row is the card-level "+" button's job (see SelectedMemberCard).
 *
 * Picker visuals (inline-link trigger, hover/open active style, picker
 * dialog styling) live in `LinkDateTimePicker.tsx` — colocated with this
 * row because the design is feature-specific. The shared `DateTimeInput`
 * is intentionally NOT reused here — its TextField rendering doesn't
 * match the inline-link spec.
 *
 * Per locked decision Q3: timezone source is RESOURCE timezone via
 * `useGlobalStore((s) => s.timezone)` (not MSP timezone).
 *
 * State: this component is pure-presentational. Slot state lives in the
 * drawer's `selectedMap` (no atoms, no Provider). Mutations are emitted
 * via onChangeStart / onChangeEnd / onDelete.
 */

import { Box, IconButton, Stack, Typography, useTheme } from "@mui/material";
import { ClockIcon, DeleteIcon, RightArrow2Icon } from "components/icons";
import { useGlobalStore } from "controller/store/globalStore";
import { convertUTCtoTimezoneV2, dateTimeToUtcV2 } from "utilz/helpers";
import { INVALID_TIME_RANGE_MESSAGE } from "../hooks/useTimeRowValidation";
import type { TimeSlotDraft } from "../types";
import { formatDuration } from "../utils/formatDateTime";
import { LinkDateTimePicker } from "./LinkDateTimePicker";

interface MemberTimeRowProps {
  resourceId: string;
  slotIndex: number;
  slot: TimeSlotDraft;
  // Validation result for THIS slot, computed once per member by the parent
  // (validateMemberSlots) so range + duplicate checks share one source of
  // truth with the Save CTA gate.
  validation: { isValid: boolean; errorMessage: string | null };
  readOnly?: boolean;
  showHoverAffordances: boolean;
  // Keeps the delete icon visible but inert (e.g. the popover's lone empty row).
  deleteDisabled?: boolean;
  onChangeStart: (iso: string) => void;
  onChangeEnd: (iso: string) => void;
  onDelete: () => void;
}

export const MemberTimeRow = ({
  resourceId: _resourceId,
  slotIndex,
  slot,
  validation,
  readOnly = false,
  showHoverAffordances,
  deleteDisabled = false,
  onChangeStart,
  onChangeEnd,
  onDelete,
}: MemberTimeRowProps) => {
  const theme = useTheme();
  // Q3 locked — RESOURCE timezone, not MSP timezone.
  const timezone = useGlobalStore((state) => state.timezone);

  const { isValid, errorMessage } = validation;
  const showInlineError = !isValid && errorMessage !== null;

  const durationLabel = formatDuration(slot.startIso, slot.endIso, timezone);

  // Hover affordance: CSS-only per CORRECTIONS. Hidden when readOnly or when
  // the caller suppresses (e.g. inactive member). Delete is available on every
  // row including the only one — removing the last row returns the member to
  // the "selected, no schedule" state (same end-state as the header clear
  // toggle). A caller can pass `deleteDisabled` to keep it visible but inert.
  const showDeleteAffordance = showHoverAffordances && !readOnly;

  return (
    <Stack sx={{ gap: "4px" }}>
      <Stack
        direction="row"
        alignItems="center"
        sx={{
          gap: "8px",
          p: "12px 16px",
          width: "100%",
          maxWidth: "642px",
          height: "48px",
          borderRadius: "8px",
          backgroundColor: theme.palette.neutral.main,
          border: `1px solid ${theme.palette.neutral["005"]}`,
          boxShadow: "0px 0px 4px 0px #00000029, 0px 0px 2px 0px #00000014",
        }}
      >
        {/* Start picker — inline link styling */}
        <Box sx={{ minWidth: 0 }}>
          <LinkDateTimePicker
            value={convertUTCtoTimezoneV2(slot.startIso || null, timezone)}
            timezone={timezone}
            disabled={readOnly}
            // AAC-580 — back-dating is ALLOWED. Dispatchers routinely log work
            // that has already happened, and `disablePast` made every past slot
            // unselectable, so a schedule could not be recorded after the fact.
            // The end-after-start rule below is a separate constraint and stays.
            error={showInlineError}
            placeholder="Start date & time"
            ariaLabel={`Start date and time for slot ${slotIndex + 1}`}
            onChange={(value) => {
              const iso = dateTimeToUtcV2(value ?? null, timezone) ?? "";
              onChangeStart(iso);
            }}
          />
        </Box>
        {/* Arrow */}
        <RightArrow2Icon
          sx={{ fontSize: "24px", color: theme.palette.lightBg.medium }}
        />
        {/* End picker — inline link styling. Disabled until a start time is
            picked: the user must add Start before End (end-before-start is not
            allowed). Once Start is set, `updateSlot` auto-fills End to
            start + 1h, so this enables with a sensible default already in.
            minDateTime is start + 1 minute so End must be STRICTLY after Start
            (equal-to-start is not selectable in the picker). */}
        <Box sx={{ minWidth: 0 }}>
          <LinkDateTimePicker
            value={convertUTCtoTimezoneV2(slot.endIso || null, timezone)}
            timezone={timezone}
            disabled={readOnly || !slot.startIso}
            // AAC-580 — see the Start picker. `minDateTime` below still forces
            // End to be strictly after Start; only the past restriction is gone.
            error={showInlineError}
            minDateTime={
              slot.startIso
                ? (convertUTCtoTimezoneV2(slot.startIso, timezone)?.add(
                    1,
                    "minute",
                  ) ?? null)
                : null
            }
            placeholder="End date & time"
            ariaLabel={`End date and time for slot ${slotIndex + 1}`}
            onChange={(value) => {
              const iso = dateTimeToUtcV2(value ?? null, timezone) ?? "";
              onChangeEnd(iso);
            }}
          />
        </Box>
        {/* Right cluster: duration+clock indicator, then the hover-revealed
            delete button. Hover scope lives on the parent SelectedMemberCard
            so hovering anywhere on the card reveals icons across every slot
            simultaneously (not per-row). `.time-row__icons` is the toggle
            handle (default opacity 0; parent card sets it to 1 on hover). */}
        <Stack
          direction="row"
          alignItems="center"
          sx={{ gap: "16px", flexShrink: 0, ml: "auto" }}
        >
          <Stack direction="row" alignItems="center" sx={{ gap: "4px" }}>
            <Typography
              variant="body-small"
              sx={{
                color: theme.palette.lightBg.low,
                whiteSpace: "nowrap",
              }}
            >
              {durationLabel}
            </Typography>
            <ClockIcon
              sx={{ fontSize: "16px", color: theme.palette.lightBg.low }}
            />
          </Stack>
          {showDeleteAffordance && (
            <IconButton
              className="time-row__icons"
              onClick={onDelete}
              disabled={deleteDisabled}
              aria-label={`Remove schedule ${slotIndex + 1}`}
              disableRipple
              sx={{
                p: 0,
                "&:hover": { backgroundColor: "transparent" },
                // MUI sets pointer-events: none on disabled; re-enable so the
                // cursor shows (a native disabled button still ignores clicks).
                "&.Mui-disabled": {
                  pointerEvents: "auto",
                  cursor: "not-allowed",
                },
              }}
            >
              <DeleteIcon
                style={{
                  width: 24,
                  height: 24,
                  color: deleteDisabled
                    ? theme.palette.lightBg.low
                    : theme.palette.lightBg.high,
                }}
              />
            </IconButton>
          )}
        </Stack>
      </Stack>

      {/* Inline validation error — surfaces beneath the row (matches
          DateTimeInput's existing helper-text convention). */}
      {showInlineError && (
        <Typography
          variant="body-small"
          role="alert"
          sx={{ color: theme.palette.error.main, pl: "12px" }}
        >
          {errorMessage ?? INVALID_TIME_RANGE_MESSAGE}
        </Typography>
      )}
    </Stack>
  );
};
